// components/business/BusinessCardOneScreen.tsx

import BusinessLocationRow from "@/components/location/BusinessLocationRow";
import { WEB_BASE_DOMAIN } from "@/lib/config/brand";
import { formatItemPrice, type ItemPricingFields } from "@/lib/itemPricing";
import type { Business } from "@/lib/types";
import { buildWhatsAppCta } from "@/lib/whatsapp";
import {
  Bookmark,
  Download,
  Flag,
  Info,
  Phone,
  Share2,
} from "lucide-react-native";

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Animated,
    Easing,
    Image,
    Linking,
    Platform,
    Pressable,
    ScrollView,
    Text,
    View,
} from "react-native";
import QRCode from "react-native-qrcode-svg";

import { useBusinessOpenStatus } from "@/lib/useBusinessOpenStatus";
import AboutUsPopup from "./AboutUsPopup";
import BusinessReportModal from "./BusinessReportModal";
import ItemImageViewerModal from "./ItemImageViewerModal";
import type { ItemImageViewerEntry } from "./ItemImageViewerModal";
import SocialIcon from "./SocialIcon";
import StarRating from "./StarRating";
import { usePublicBusinessCardActions } from "./usePublicBusinessCardActions";
import type { BusinessCardActionHandler } from "./usePublicBusinessCardActions";

type Item = ItemPricingFields & {
  name?: string;
  title?: string;
  img?: string;
  image?: string;
  imageUrl?: string;
  imageURI?: string;
  imageUri?: string;
  photoUrl?: string;
  imageFit?: "cover" | "contain";
};

type ItemImageGalleryEntry = ItemImageViewerEntry & {
  itemIndex: number;
};

type Props = {
  business: Business;
  onShare?: BusinessCardActionHandler;
  onSave?: BusinessCardActionHandler;
  onDownload?: BusinessCardActionHandler;
};

type SocialIconLink = {
  key: string;
  label: string;
  url: string;
  platform: string;
};

function normalizeSubCategory(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((s) => String(s || "").trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    const t = value.trim();
    return t ? [t] : [];
  }

  return [];
}

function cleanText(value: unknown) {
  return String(value ?? "").trim();
}

function normalizeKey(value: string) {
  return value.toLowerCase().replace(/\s+/g, " ").trim();
}

function splitCommaParts(value: string) {
  return value
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
}

function uniqueCleanParts(parts: string[]) {
  const seen = new Set<string>();

  return parts.filter((part) => {
    const value = cleanText(part);
    const key = normalizeKey(value);

    if (!key) return false;
    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
}

function removeCountyParts(parts: string[], countyName?: string | null) {
  const county = cleanText(countyName);
  const countyKey = normalizeKey(county);
  const countyWithoutWordKey = normalizeKey(county.replace(/\s+county$/i, ""));

  return parts.filter((part) => {
    const value = cleanText(part);
    const key = normalizeKey(value);

    if (!key) return false;
    if (key.endsWith(" county")) return false;
    if (countyKey && key === countyKey) return false;
    if (countyWithoutWordKey && key === countyWithoutWordKey) return false;

    return true;
  });
}

function getAreaCityLabel(business: Business) {
  const b = business as any;
  const loc = b.location || {};

  const areaName = cleanText(
    loc.areaName ||
      loc.areaSuggestionName ||
      b.areaName ||
      b.areaSuggestionName
  );

  const cityName = cleanText(
    loc.cityName ||
      loc.citySuggestionName ||
      b.cityName ||
      b.citySuggestionName
  );

  const countyName = cleanText(loc.countyName || b.countyName);
  const cityLine = cleanText(b.cityLine);

  const directParts = uniqueCleanParts([areaName, cityName]);
  const cityLineParts = removeCountyParts(splitCommaParts(cityLine), countyName);

  const finalParts = uniqueCleanParts([...directParts, ...cityLineParts]);

  return finalParts.slice(0, 2).join(", ");
}

function getBusinessTypeDisplayLabel(businessType: unknown) {
  if (businessType === "online_only") return "Online Shop";
  if (businessType === "freelancer") return "Freelance";
  return "";
}

function hasValidCoordinates(lat: unknown, lng: unknown) {
  return (
    typeof lat === "number" &&
    Number.isFinite(lat) &&
    typeof lng === "number" &&
    Number.isFinite(lng)
  );
}

function buildLocationDisplay(business: Business) {
  const b = business as any;

  const businessType = b.businessType;
  const businessName = cleanText(b.name);
  const addressLineRaw = cleanText(b.addressLine);
  const areaCity = getAreaCityLabel(business);
  const typeLabel = getBusinessTypeDisplayLabel(businessType);

  const addressLine =
    addressLineRaw && normalizeKey(addressLineRaw) !== normalizeKey(businessName)
      ? addressLineRaw
      : "";

  const lat = b.locationPin?.lat;
  const lng = b.locationPin?.lng;
  const hasCoords = hasValidCoordinates(lat, lng);

  const isPhysicalShop = businessType === "physical_shop";
  const isOnlineOrFreelance =
    businessType === "online_only" || businessType === "freelancer";

  if (isOnlineOrFreelance) {
    return {
      line1: typeLabel,
      line2: areaCity,
      mapAddress: areaCity,
      mapTitle: businessName || typeLabel,
      mapSubtitle: areaCity,
      canOpenMap: false,
      hasLocationText: !!typeLabel || !!areaCity,
    };
  }

  const physicalLine1 = addressLine || areaCity || "Business location";
  const physicalLine2 = addressLine ? areaCity : "";

  return {
    line1: physicalLine1,
    line2: physicalLine2,
    mapAddress: addressLine || areaCity,
    mapTitle: businessName || "Business location",
    mapSubtitle: addressLine ? areaCity : "",
    canOpenMap: isPhysicalShop && hasCoords,
    hasLocationText: !!physicalLine1 || !!physicalLine2 || hasCoords,
  };
}

function safeUrl(url?: string | null) {
  const u = String(url || "").trim();
  return u ? u : undefined;
}

function normalizeWebUrl(url?: string | null) {
  const u = safeUrl(url);
  if (!u) return undefined;

  if (/^(https?:\/\/|tel:|mailto:|sms:|whatsapp:)/i.test(u)) {
    return u;
  }

  return `https://${u}`;
}

function normalizeEmailUrl(value?: string | null) {
  const raw = safeUrl(value);
  if (!raw) return undefined;

  if (/^mailto:/i.test(raw)) return raw;

  const email = raw.replace(/^mailto:/i, "").trim();
  if (!email) return undefined;

  return `mailto:${email}`;
}

function normalizePhoneForTel(phone?: string | null) {
  const raw = String(phone || "").trim();
  if (!raw) return undefined;

  const cleaned = raw.replace(/[^\d+]/g, "");

  if (/^0\d{9}$/.test(cleaned)) {
    return `+254${cleaned.slice(1)}`;
  }

  if (/^[17]\d{8}$/.test(cleaned)) {
    return `+254${cleaned}`;
  }

  if (/^254\d{9}$/.test(cleaned)) {
    return `+${cleaned}`;
  }

  return cleaned;
}

function normalizeSocialUrl(
  value?: string | null,
  platform?:
    | "facebook"
    | "instagram"
    | "linkedin"
    | "twitter"
    | "tiktok"
    | "youtube"
) {
  const raw = safeUrl(value);
  if (!raw) return undefined;

  if (/^https?:\/\//i.test(raw)) return raw;

  const cleaned = raw.replace(/^@/, "").replace(/^\/+/, "");

  if (raw.includes(".")) {
    return `https://${raw}`;
  }

  if (platform === "facebook") return `https://facebook.com/${cleaned}`;
  if (platform === "instagram") return `https://instagram.com/${cleaned}`;
  if (platform === "linkedin") return `https://linkedin.com/in/${cleaned}`;
  if (platform === "twitter") return `https://x.com/${cleaned}`;
  if (platform === "tiktok") return `https://tiktok.com/@${cleaned}`;

  if (platform === "youtube") {
    if (
      cleaned.startsWith("@") ||
      cleaned.startsWith("c/") ||
      cleaned.startsWith("channel/") ||
      cleaned.startsWith("user/")
    ) {
      return `https://youtube.com/${cleaned}`;
    }

    return `https://youtube.com/@${cleaned}`;
  }

  return normalizeWebUrl(raw);
}

async function openLink(url?: string | null) {
  const u = safeUrl(url);
  if (!u) return;

  try {
    await Linking.openURL(u);
  } catch (e) {
    console.log("openLink failed:", u, e);
    Alert.alert(
      "Could not open link",
      "Please check that the link or phone number is valid."
    );
  }
}

function getItemImage(it: any) {
  const candidates = [
    it?.img,
    it?.imageUrl,
    it?.imageURI,
    it?.imageUri,
    it?.image,
    it?.photoUrl,
  ];

  for (const c of candidates) {
    const t = String(c ?? "").trim();
    if (t) return t;
  }

  return "";
}

function getImageResizeMode(value: unknown): "cover" | "contain" {
  return value === "contain" ? "contain" : "cover";
}

function getDomainLabel(url?: string) {
  if (!url) return "";

  return url
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/+$/g, "")
    .trim();
}

/**
 * AutoMarqueeText
 *
 * Used for category + sub-category.
 * - If the text fits, it stays still.
 * - If the text is too long, it scrolls right-to-left.
 * - It avoids the "..." issue by measuring the real text width separately.
 */
function AutoMarqueeText({
  text,
  style,
  speed = 24,
  pauseMs = 900,
}: {
  text: string;
  style?: any;
  speed?: number;
  pauseMs?: number;
}) {
  const translateX = useRef(new Animated.Value(0)).current;

  const [containerWidth, setContainerWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);

  const gap = 42;
  const shouldScroll = containerWidth > 0 && contentWidth > containerWidth + 6;

  useEffect(() => {
    translateX.stopAnimation();
    translateX.setValue(0);

    if (!shouldScroll) return;

    const distance = contentWidth + gap;
    const duration = Math.max(8500, distance * speed);

    const animation = Animated.loop(
      Animated.sequence([
        Animated.delay(pauseMs),
        Animated.timing(translateX, {
          toValue: -distance,
          duration,
          easing: Easing.linear,
          useNativeDriver: Platform.OS !== "web",
        }),
        Animated.delay(pauseMs),
        Animated.timing(translateX, {
          toValue: 0,
          duration: 1,
          easing: Easing.linear,
          useNativeDriver: Platform.OS !== "web",
        }),
      ])
    );

    animation.start();

    return () => {
      animation.stop();
      translateX.stopAnimation();
      translateX.setValue(0);
    };
  }, [contentWidth, pauseMs, shouldScroll, speed, translateX]);

  return (
    <View
      style={styles.marqueeOuter}
      onLayout={(event) => {
        const width = event.nativeEvent.layout.width || 0;

        if (width && Math.abs(width - containerWidth) > 2) {
          setContainerWidth(width);
        }
      }}
    >
      <View pointerEvents="none" style={styles.marqueeMeasureWrap}>
        <Text
          style={[style, styles.marqueeMeasureText]}
          numberOfLines={1}
          ellipsizeMode="clip"
          onTextLayout={(event) => {
            const measuredWidth = event.nativeEvent.lines?.[0]?.width || 0;

            if (measuredWidth && Math.abs(measuredWidth - contentWidth) > 2) {
              setContentWidth(measuredWidth);
            }
          }}
        >
          {text}
        </Text>
      </View>

      <Animated.View
        pointerEvents="none"
        style={[
          styles.marqueeInner,
          shouldScroll
            ? {
                width: contentWidth * 2 + gap,
                transform: [{ translateX }],
              }
            : {
                width: "100%",
              },
        ]}
      >
        <Text
          style={[
            style,
            styles.marqueeText,
            shouldScroll ? { width: contentWidth } : { width: "100%" },
          ]}
          numberOfLines={1}
          ellipsizeMode={shouldScroll ? "clip" : "tail"}
        >
          {text}
        </Text>

        {shouldScroll ? (
          <Text
            style={[
              style,
              styles.marqueeText,
              {
                width: contentWidth,
                marginLeft: gap,
              },
            ]}
            numberOfLines={1}
            ellipsizeMode="clip"
          >
            {text}
          </Text>
        ) : null}
      </Animated.View>
    </View>
  );
}

export default function BusinessCardOneScreen({
  business,
  onShare,
  onSave,
  onDownload,
}: Props) {
  const cardRef = useRef<View>(null);
  const [webSocialWidth, setWebSocialWidth] = useState(0);
  const [aboutVisible, setAboutVisible] = useState(false);
  const [viewerInitialIndex, setViewerInitialIndex] = useState<number | null>(
    null
  );
  const {
    saved,
    publicUrl,
    handleShare,
    handleSave,
    handleDownload,
    handleReport,
    reportModalProps,
  } = usePublicBusinessCardActions({
    business,
    cardRef,
    onShare,
    onSave,
    onDownload,
  });

  const openStatus = useBusinessOpenStatus(business);

  const subArr = useMemo(
    () =>
      normalizeSubCategory(
        (business as any).subCategory ?? (business as any).subCategories
      ),
    [business]
  );

  const metaLine = useMemo(() => {
    const cat =
      cleanText((business as any).categoryName) ||
      cleanText((business as any).category);

    const sub =
      cleanText((business as any).subcategoryName) || subArr.join(" · ");

    return [cat, sub].filter(Boolean).join(" • ");
  }, [business, subArr]);

  const items = useMemo(() => {
    const raw = (business as any).items;
    return Array.isArray(raw) ? (raw as Item[]) : [];
  }, [business]);

  const itemImageGallery = useMemo<ItemImageGalleryEntry[]>(() => {
    const entries: ItemImageGalleryEntry[] = [];

    items.forEach((it, idx) => {
      const uri = getItemImage(it);
      if (!uri) return;

      const title = cleanText(it?.name || it?.title || `Item ${idx + 1}`);
      const description = cleanText(it?.description);
      const priceText = formatItemPrice(it);

      entries.push({
        key: `${idx}-${uri}`,
        itemIndex: idx,
        title,
        uri,
        priceText,
        description,
      });
    });

    return entries;
  }, [items]);

  const openItemImageViewer = React.useCallback(
    (itemIndex: number) => {
      const galleryIndex = itemImageGallery.findIndex(
        (item) => item.itemIndex === itemIndex
      );

      if (galleryIndex >= 0) {
        setViewerInitialIndex(galleryIndex);
      }
    },
    [itemImageGallery]
  );

  const closeItemImageViewer = React.useCallback(() => {
    setViewerInitialIndex(null);
  }, []);

  const phoneNumber = normalizePhoneForTel((business as any).phone);
  const shortDescription = cleanText((business as any).shortDescription);

  const websiteUrl = normalizeWebUrl(
    (business as any).website || (business as any).websiteUrl
  );

  const websiteDisplayUrl = websiteUrl || publicUrl;

  const websiteDomain = websiteUrl
    ? getDomainLabel(websiteUrl)
    : WEB_BASE_DOMAIN;

  const facebookUrl = normalizeSocialUrl(
    (business as any).facebook || (business as any).facebookUrl,
    "facebook"
  );

  const instagramUrl = normalizeSocialUrl(
    (business as any).instagram || (business as any).instagramUrl,
    "instagram"
  );

  const twitterUrl = normalizeSocialUrl(
    (business as any).twitter ||
      (business as any).x ||
      (business as any).xUrl ||
      (business as any).twitterUrl,
    "twitter"
  );

  const tiktokUrl = normalizeSocialUrl(
    (business as any).tiktok || (business as any).tiktokUrl,
    "tiktok"
  );

  const linkedinUrl = normalizeSocialUrl(
    (business as any).linkedin ||
      (business as any).linkedIn ||
      (business as any).linkedinUrl ||
      (business as any).linkedInUrl,
    "linkedin"
  );

  const youtubeUrl = normalizeSocialUrl(
    (business as any).youtube || (business as any).youtubeUrl,
    "youtube"
  );

  const emailUrl = normalizeEmailUrl(
    (business as any).email || (business as any).businessEmail
  );

  const socialLinks = useMemo<SocialIconLink[]>(() => {
    return [
      {
        key: "email",
        label: "Email",
        url: emailUrl || "",
        platform: "email",
      },
      {
        key: "facebook",
        label: "Facebook",
        url: facebookUrl || "",
        platform: "facebook",
      },
      {
        key: "instagram",
        label: "Instagram",
        url: instagramUrl || "",
        platform: "instagram",
      },
      {
        key: "tiktok",
        label: "TikTok",
        url: tiktokUrl || "",
        platform: "tiktok",
      },
      {
        key: "linkedin",
        label: "LinkedIn",
        url: linkedinUrl || "",
        platform: "linkedin",
      },
      {
        key: "youtube",
        label: "YouTube",
        url: youtubeUrl || "",
        platform: "youtube",
      },
      {
        key: "twitter",
        label: "X",
        url: twitterUrl || "",
        platform: "twitter",
      },
    ].filter((item) => !!item.url);
  }, [
    emailUrl,
    facebookUrl,
    instagramUrl,
    linkedinUrl,
    tiktokUrl,
    youtubeUrl,
    twitterUrl,
  ]);

  const socialIconsWidth =
    socialLinks.length * 30 + Math.max(0, socialLinks.length - 1) * 8;

  const isShortWebsiteLabel =
    !!websiteDomain && websiteDomain.length <= 20 && !websiteDomain.includes("/");

  const availableWebsiteWidth = webSocialWidth - socialIconsWidth - 8;

  const showWebsiteText =
    isShortWebsiteLabel && webSocialWidth > 0 && availableWebsiteWidth >= 118;

  const whatsappCta = buildWhatsAppCta({
    whatsappCtaType: (business as any).whatsappCtaType,
    whatsappChatNumber: (business as any).whatsappChatNumber,
    whatsappCatalogUrl: (business as any).whatsappCatalogUrl,
  });

  const locationLines = useMemo(
    () => buildLocationDisplay(business),
    [business]
  );

  const locationPin = (business as any).locationPin;

  const hasLocationText =
    locationLines.hasLocationText ||
    hasValidCoordinates(locationPin?.lat, locationPin?.lng);

  const logoFit = getImageResizeMode((business as any).logoFit || "cover");
  const LastActionIcon = Platform.OS === "web" ? Flag : Download;
  const hasPrimaryActions = !!shortDescription || !!phoneNumber;

  return (
    <View style={{ width: "100%", paddingVertical: 10, paddingHorizontal: 12 }}>
      <View ref={cardRef} collapsable={false} style={styles.card}>
        <View style={styles.headerRow}>
          <Image
            source={{
              uri:
                (business as any).logoUrl ||
                "https://via.placeholder.com/120x120.png?text=LOGO",
            }}
            style={styles.logo}
            resizeMode={logoFit}
          />

          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.name} numberOfLines={2}>
              {cleanText((business as any).name) || "Business Name"}
            </Text>

            {!!metaLine && (
              <AutoMarqueeText text={metaLine} style={styles.meta} />
            )}

            <View style={{ marginTop: 4 }}>
              <StarRating
                rating={Number((business as any).rating || 0)}
                reviewCount={Number((business as any).reviewCount || 0)}
              />
            </View>

            <View style={styles.openRow}>
              <View
                style={[
                  styles.dot,
                  {
                    backgroundColor:
                      openStatus.tone === "open"
                        ? "#16a34a"
                        : openStatus.tone === "closed"
                        ? "#ef4444"
                        : "#9ca3af",
                  },
                ]}
              />

              <Text style={styles.openText} numberOfLines={1}>
                {openStatus.fullText}
              </Text>
            </View>
          </View>
        </View>

        {hasLocationText && (
          <View style={styles.locationWrap}>
            <BusinessLocationRow
              businessName={cleanText((business as any).name) || null}
              lat={locationLines.canOpenMap ? locationPin?.lat ?? null : null}
              lng={locationLines.canOpenMap ? locationPin?.lng ?? null : null}
              mapTitle={locationLines.mapTitle || null}
              mapSubtitle={locationLines.mapSubtitle || null}
              addressLine={locationLines.mapAddress || null}
              line1={locationLines.line1}
              line2={locationLines.line2 || null}
              canOpenMap={locationLines.canOpenMap}
            />
          </View>
        )}

        {hasPrimaryActions && (
          <View style={styles.primaryActionsRow}>
            {!!shortDescription && (
              <Pressable
                onPress={() => setAboutVisible(true)}
                style={styles.aboutBtn}
                accessibilityLabel="Open About Us"
              >
                <Info size={16} color="#111827" strokeWidth={2.5} />
                <Text style={styles.aboutBtnText}>About Us</Text>
              </Pressable>
            )}

            {!!phoneNumber && (
              <Pressable
                onPress={() => openLink(`tel:${phoneNumber}`)}
                style={styles.callBtn}
                accessibilityLabel="Call business"
              >
                <Phone size={16} color="#ffffff" strokeWidth={2.5} />
                <Text style={styles.callBtnText}>Call Now</Text>
              </Pressable>
            )}
          </View>
        )}

        <View style={styles.sectionHeaderRow}>
          <Text style={styles.sectionTitle}>Our Products</Text>

          <View style={styles.swipeHint}>
            <Text style={styles.swipeHintText}>Swipe</Text>
            <Text style={styles.swipeHintArrow}>→</Text>
          </View>
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={[styles.productsRow, { marginTop: 8 }]}
        >
          {items.length ? (
            items.map((it, idx) => {
              const title = cleanText(it?.name || it?.title || "Item") || "Item";
              const img = getItemImage(it);
              const imageFit = getImageResizeMode(it?.imageFit);

              return (
                <View key={`${title}-${idx}`} style={styles.productCard}>
                  {img ? (
                    <Pressable
                      onPress={() => openItemImageViewer(idx)}
                      style={({ pressed }) => [
                        pressed ? styles.productImgPressed : null,
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel={`Open ${title} image`}
                    >
                      <Image
                        source={{ uri: img }}
                        style={styles.productImg}
                        resizeMode={imageFit}
                      />
                    </Pressable>
                  ) : (
                    <Image
                      source={{
                        uri: "https://via.placeholder.com/600x400?text=Item",
                      }}
                      style={styles.productImg}
                      resizeMode={imageFit}
                    />
                  )}

                  <Text style={styles.productName} numberOfLines={1}>
                    {title}
                  </Text>
                </View>
              );
            })
          ) : (
            <View style={[styles.productCard, { justifyContent: "center" }]}>
              <Text style={[styles.productName, { textAlign: "center" }]}>
                No products yet
              </Text>
            </View>
          )}
        </ScrollView>

        <View
          style={styles.webSocialRow}
          onLayout={(event) => {
            const width = event?.nativeEvent?.layout?.width || 0;

            if (width && Math.abs(width - webSocialWidth) > 2) {
              setWebSocialWidth(width);
            }
          }}
        >
          <Pressable
            onPress={() => openLink(websiteDisplayUrl)}
            style={[
              styles.websiteBtn,
              showWebsiteText ? styles.websiteBtnWide : styles.websiteIconBtn,
            ]}
            accessibilityLabel="Open website"
          >
            <SocialIcon
              platform="website"
              url={websiteDisplayUrl}
              size={16}
              color="#1d4ed8"
            />

            {showWebsiteText ? (
              <Text style={styles.websiteText} numberOfLines={1}>
                {websiteDomain}
              </Text>
            ) : null}
          </Pressable>

          <View style={styles.socialIconsRow}>
            {socialLinks.map((item) => (
              <Pressable
                key={item.key}
                onPress={() => openLink(item.url)}
                style={styles.iconBtn}
                accessibilityLabel={item.label}
              >
                <SocialIcon
                  platform={item.platform}
                  url={item.url}
                  size={16}
                  color="#111827"
                />
              </Pressable>
            ))}
          </View>
        </View>

        <View style={styles.qrRow}>
          <View style={styles.qrBox}>
            <QRCode value={publicUrl} size={62} />
          </View>

          <Pressable
            onPress={() => openLink(whatsappCta.url)}
            disabled={!whatsappCta.url}
            style={[styles.waBtn, !whatsappCta.url && { opacity: 0.6 }]}
          >
            <SocialIcon
              platform="whatsapp"
              url={whatsappCta.url}
              size={16}
              color="#ffffff"
            />

            <Text style={styles.waBtnText} numberOfLines={1}>
              {whatsappCta.label}
            </Text>
          </Pressable>
        </View>

        <View style={styles.actionsRow}>
          <Pressable
            onPress={handleShare}
            style={styles.actionBtn}
            accessibilityLabel="Share business"
          >
            <Share2 size={15} color="#111827" strokeWidth={2.5} />
            <Text style={styles.actionText}>Share</Text>
          </Pressable>

          <Pressable
            onPress={handleSave}
            style={styles.actionBtn}
            accessibilityLabel={saved ? "Remove saved business" : "Save business"}
          >
            <Bookmark
              size={15}
              color="#111827"
              fill={saved ? "#111827" : "none"}
              strokeWidth={2.5}
            />
            <Text style={styles.actionText}>{saved ? "Saved" : "Save"}</Text>
          </Pressable>

          <Pressable
            onPress={Platform.OS === "web" ? handleReport : handleDownload}
            style={styles.actionBtn}
            accessibilityLabel={Platform.OS === "web" ? "Report business" : "Download card"}
          >
            <LastActionIcon size={15} color="#111827" strokeWidth={2.5} />
            <Text style={styles.actionText}>
              {Platform.OS === "web" ? "Report" : "Download"}
            </Text>
          </Pressable>
        </View>
      </View>
      <BusinessReportModal {...reportModalProps} />
      <AboutUsPopup
        visible={aboutVisible}
        shortDescription={shortDescription}
        onClose={() => setAboutVisible(false)}
      />
      <ItemImageViewerModal
        visible={viewerInitialIndex !== null}
        items={itemImageGallery}
        initialIndex={viewerInitialIndex ?? 0}
        onClose={closeItemImageViewer}
      />
    </View>
  );
}

const styles: any = {
  card: {
    width: "100%",
    maxWidth: 720,
    alignSelf: "center",
    borderRadius: 18,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#fff",
    padding: 12,
  },

  headerRow: {
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-start",
  },

  logo: {
    width: 56,
    height: 56,
    borderRadius: 14,
    backgroundColor: "#e5e7eb",
  },

  name: {
    fontSize: 16,
    fontWeight: "900",
    color: "#111827",
  },

  meta: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: "800",
    color: "#6b7280",
  },

  marqueeOuter: {
    marginTop: 2,
    width: "100%",
    height: 16,
    overflow: "hidden",
    justifyContent: "center",
  },

  marqueeMeasureWrap: {
    position: "absolute",
    left: 0,
    top: 0,
    width: 10000,
    opacity: 0,
    zIndex: -1,
  },

  marqueeMeasureText: {
    flexShrink: 0,
    alignSelf: "flex-start",
  },

  marqueeInner: {
    flexDirection: "row",
    alignItems: "center",
  },

  marqueeText: {
    flexShrink: 0,
  },

  openRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 4,
  },

  dot: {
    width: 9,
    height: 9,
    borderRadius: 99,
  },

  openText: {
    fontSize: 12,
    fontWeight: "800",
    color: "#374151",
    flex: 1,
  },

  locationWrap: {
    marginTop: 8,
  },

  primaryActionsRow: {
    marginTop: 8,
    flexDirection: "row",
    gap: 8,
  },

  aboutBtn: {
    flex: 1,
    minWidth: 0,
    height: 38,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },

  aboutBtnText: {
    color: "#111827",
    fontWeight: "900",
    fontSize: 12,
  },

  callBtn: {
    flex: 1,
    minWidth: 0,
    height: 38,
    borderRadius: 12,
    backgroundColor: "#2563eb",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },

  callBtnText: {
    color: "#fff",
    fontWeight: "900",
    fontSize: 12,
  },

  sectionHeaderRow: {
    marginTop: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },

  sectionTitle: {
    fontSize: 12,
    fontWeight: "900",
    color: "#111827",
  },

  swipeHint: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },

  swipeHintText: {
    fontSize: 11,
    fontWeight: "800",
    color: "#6b7280",
  },

  swipeHintArrow: {
    fontSize: 14,
    fontWeight: "900",
    color: "#6b7280",
  },

  productsRow: {
    paddingRight: 8,
    gap: 10,
  },

  productCard: {
    width: 150,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    overflow: "hidden",
    backgroundColor: "#fff",
  },

  productImg: {
    width: "100%",
    height: 104,
    backgroundColor: "#f3f4f6",
  },

  productImgPressed: {
    opacity: 0.9,
  },

  productName: {
    padding: 8,
    fontSize: 11,
    fontWeight: "900",
    color: "#111827",
  },

  webSocialRow: {
    marginTop: 10,
    minHeight: 32,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },

  websiteBtn: {
    height: 30,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },

  websiteBtnWide: {
    flex: 1,
    minWidth: 0,
    paddingHorizontal: 9,
    justifyContent: "flex-start",
    gap: 5,
  },

  websiteIconBtn: {
    width: 30,
    paddingHorizontal: 0,
  },

  websiteText: {
    flex: 1,
    minWidth: 0,
    fontSize: 11,
    fontWeight: "900",
    color: "#1d4ed8",
  },

  socialIconsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },

  iconBtn: {
    width: 30,
    height: 30,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
  },

  qrRow: {
    marginTop: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },

  qrBox: {
    width: 68,
    height: 68,
    borderRadius: 14,
    backgroundColor: "#f3f4f6",
    alignItems: "center",
    justifyContent: "center",
  },

  waBtn: {
    flex: 1,
    minWidth: 0,
    height: 44,
    borderRadius: 14,
    backgroundColor: "#16a34a",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
    flexDirection: "row",
    gap: 7,
  },

  waBtnText: {
    color: "#fff",
    fontWeight: "900",
    fontSize: 11,
    flexShrink: 1,
  },

  actionsRow: {
    marginTop: 10,
    flexDirection: "row",
    gap: 8,
  },

  actionBtn: {
    flex: 1,
    height: 34,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#f7f7f7",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
  },

  actionText: {
    fontSize: 11,
    fontWeight: "900",
    color: "#111827",
  },

};
